const ProductsService = {
  /**
   * Get products
   * with type if specified
   * @param {Object} payload
   * @returns {Object}
   */
  getProducts: async (payload) => {
    const params = Object.keys(payload).map(key => `${encodeURIComponent(key)}=${encodeURIComponent(payload[key])}`).join('&');
    const { data, headers } = await ovpFetch(`/products?${params}`);
    const pagination = {
      max_page: 1,
      current_page: payload.page,
    };

    const { link } = headers;
    if (link) {
      const linkSplit = link.split(',');
      const lastIndex = linkSplit.findIndex(rel => rel.includes('rel="last"'));
      const prevIndex = linkSplit.findIndex(rel => rel.includes('rel="prev"'));
      if (lastIndex > -1) {
        pagination.max_page = parseInt(linkSplit[lastIndex].match(/(?:&page=|\?page=)(\d+)/)[1], 10);
      } else if (prevIndex > -1) {
        pagination.max_page = parseInt(linkSplit[prevIndex].match(/(?:&page=|\?page=)(\d+)/)[1], 10) + 1;
      }
      pagination.current_page = pagination.max_page < pagination.current_page ? pagination.max_page : pagination.current_page;
    }

    return {
      products: data,
      pagination,
    };
  },
  /**
   * Get orders
   * @param {Object} payload
   * @returns {Object}
   */
  getOrders: async (payload) => {
    const params = Object.keys(payload).map(key => `${encodeURIComponent(key)}=${encodeURIComponent(payload[key])}`).join('&');
    const { data, headers } = await ovpFetch(`/orders?${params}`);
    const pagination = {
      max_page: 1,
      current_page: payload.page,
    };

    const { link } = headers;
    if (link) {
      const linkSplit = link.split(',');
      const lastIndex = linkSplit.findIndex(rel => rel.includes('rel="last"'));
      const prevIndex = linkSplit.findIndex(rel => rel.includes('rel="prev"'));
      if (lastIndex > -1) {
        pagination.max_page = parseInt(linkSplit[lastIndex].match(/(?:&page=|\?page=)(\d+)/)[1], 10);
      } else if (prevIndex > -1) {
        pagination.max_page = parseInt(linkSplit[prevIndex].match(/(?:&page=|\?page=)(\d+)/)[1], 10) + 1;
      }
      pagination.current_page = pagination.max_page < pagination.current_page ? pagination.max_page : pagination.current_page;
    }

    return {
      orders: data,
      pagination,
    };
  },
  /**
   * Get single order
   * @param {String} id
   * @return {Object}
   */
  getOrder: async (id) => {
    const { data } = await ovpFetch(`/orders/${id}`);
    return data;
  },
  /**
   * Fulfill order
   * @param {String} id
   * @param {Object} payload
   * @returns {Boolean}
   */
  fulfillOrder: async (id, payload) => {
    await ovpFetch(`/orders/${id}/fulfilled`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return true;
  },
  /**
   * Special get all tickets (streamtype) for when broadcast wants to mingle
   * @returns {Array}
   */
  getAllStreams: async () => {
    const { data, headers } = await ovpFetch('/products?direction=desc&sort=name&type=stream&page=1&per_page=20');
    const ticketsArr = [...data];

    const { link } = headers;
    if (link) {
      const linkSplit = link.split(',');
      const lastIndex = linkSplit.findIndex(rel => rel.includes('rel="last"'));
      const maxPages = parseInt(linkSplit[lastIndex].match(/(?:&page=|\?page=)(\d+)/)[1], 10);

      if (maxPages > 1) {
        for (let i = 2; i <= maxPages; i += 1) {
          // eslint-disable-next-line
          const { data } = await ovpFetch(`/products?direction=desc&sort=name&type=stream&page=${i}&per_page=20`);
          ticketsArr.push(...data);
        }
      }
    }

    return ticketsArr;
  },
  /**
   * Special get all goods (goodtype) for when broadcast wants to mingle
   * @returns {Array}
   */
  getAllGoods: async () => {
    const { data } = await ovpFetch('/products?direction=desc&sort=name&type=good&page=1&per_page=100');
    return data;
  },
  saveMagentoFeed: async (magentoData) => {
    const data = await ovpFetch('/v2/settings/shopping/magento', {
      method: 'POST',
      iLikeItRaw: true,
      body: JSON.stringify(magentoData),
    });
    return data;
  },
  saveGoogleMerchantFeed: async (url) => {
    const data = await ovpFetch('/v2/settings/shopping/google-merchant', {
      method: 'POST',
      iLikeItRaw: true,
      body: JSON.stringify({
        url,
      }),
    });
    return data;
  },
  saveGeins: async (geinsData) => {
    const data = await ovpFetch('/v2/settings/shopping/geins', {
      method: 'POST',
      iLikeItRaw: true,
      body: JSON.stringify(geinsData),
    });
    return data;
  },
  saveCentra: async (centraData) => {
    const data = await ovpFetch('/v2/settings/shopping/centra', {
      method: 'POST',
      iLikeItRaw: true,
      body: JSON.stringify(centraData),
    });
    return data;
  },
  saveProductLinks: async () => {
    const data = await ovpFetch('/v2/settings/shopping/link', {
      method: 'POST',
      iLikeItRaw: true,
    });
    return data;
  },
  saveShopifyStore: async (store) => {
    const data = await ovpFetch('/v2/settings/shopping/shopify', {
      method: 'POST',
      iLikeItRaw: true,
      body: JSON.stringify({
        store,
      }),
    });
    return data;
  },
  // eslint-disable-next-line
  refreshGoogleMerchantFeed: async () => {
    const data = await ovpFetch('/v2/settings/shopping/google-merchant/refresh', {
      method: 'POST',
      iLikeItRaw: true,
    });
    return data;
  },
  /**
   * Create a product and return it
   * @param {Object} payload
   * @returns {Object}
   */
  createProduct: async (payload) => {
    const data = await ovpFetch('/products', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return data;
  },
  /**
   * Get a single product and return it
   * @param {String} id
   * @returns {Object}
   */
  getProduct: async (id) => {
    const { data } = await ovpFetch(`/products/${id}`);
    return data;
  },
  /**
   * Update a single product and return the new data
   * @param {String} id
   * @param {Object} payload
   * @returns {Object}
   */
  updateProduct: async (id, payload) => {
    const { data } = await ovpFetch(`/products/${id}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return data;
  },
  /**
   * Delete a single product
   * @param {String} id
   * @returns {Boolean}
   */
  deleteProduct: async (id) => {
    await ovpFetch(`/products/${id}`, {
      method: 'DELETE',
    });
    return true;
  },
  /**
   * Add an attribute to
   * or replace all attributes on
   * a product
   * @param {String} id
   * @param {Array} attributes
   * @param {Boolean} replace defaults to false
   * @returns {Boolean}
   */
  addAttributeToProduct: async (id, attributes, replace = false) => {
    await ovpFetch(`/products/${id}/attributes`, {
      method: replace ? 'POST' : 'PATCH',
      body: JSON.stringify(attributes),
    });
    return true;
  },
  /**
   * Remove an attribute, or multiple on a product
   * @param {String} id
   * @param {Array} attributes
   * @returns {Boolean}
   */
  removeAttributeOnProduct: async (id, attributes) => {
    await ovpFetch(`/products/${id}/attributes`, {
      method: 'DELETE',
      body: JSON.stringify(attributes),
    });
    return true;
  },
  /**
   * Add images to a product
   * @param {String} id
   * @param {Array} images
   * @returns {Boolean}
   */
  addImage: async (id, images) => {
    await ovpFetch(`/products/${id}/images`, {
      method: 'PATCH',
      body: JSON.stringify(images),
    });
    return true;
  },
  /**
   * Remove images from a product
   * @param {String} id
   * @param {Array} images
   * @returns {Boolean}
   */
  removeImage: async (id, images) => {
    await ovpFetch(`/products/${id}/images`, {
      method: 'DELETE',
      body: JSON.stringify(images),
    });
    return true;
  },
  /**
   * Update images on a product
   * @param {String} id
   * @param {Array} images
   * @returns {Boolean}
   */
  updateImages: async (id, images) => {
    await ovpFetch(`/products/${id}/images`, {
      method: 'POST',
      body: JSON.stringify(images),
    });
    return true;
  },
  /**
   * Update content on a product
   * @param {String} id
   * @param {Array} content
   * @returns {Boolean}
   */
  updateContent: async (id, content, patch) => {
    await ovpFetch(`/products/${id}/access/content`, {
      method: patch ? 'PATCH' : 'POST',
      body: JSON.stringify(content),
    });
    return true;
  },
  /**
   * Remove content on a product
   * @param {String} id
   * @param {Array} content
   * @returns {Boolean}
   */
  removeContent: async (id, content) => {
    await ovpFetch(`/products/${id}/access/content`, {
      method: 'DELETE',
      body: JSON.stringify(content),
    });
    return true;
  },
  /**
   * Create SKU
   * @param {Object} payload
   */
  createSKU: async (payload) => {
    const { data } = await ovpFetch('/sku', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return data;
  },
  /**
   * Update SKU
   * @param {String} id
   * @param {Object} payload
   */
  updateSKU: async (id, payload) => {
    const { data } = await ovpFetch(`/sku/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return data;
  },
  /**
   * Delete SKU
   * @param {String} id
   */
  deleteSKU: async (id) => {
    await ovpFetch(`/sku/${id}`, {
      method: 'DELETE',
    });
    return true;
  },
  //
  //
  //
  //
  //
  //
  //
  //
  //
  searchProducts: async (searchKey) => {
    const { data } = await ovpFetch(`/shopping/search?query=${searchKey}`);
    return data;
  },
  searchShopifyProducts: async (searchKey) => {
    const { data } = await ovpFetch(`/shopping/shopify/search-proxy?q=${searchKey}&resources[type]=product`);
    return data;
  },
  toggleEventProduct: async (id, payload) => {
    await ovpFetch(`/live/broadcast/${id}/product-event`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return true;
  },
  /**
   * Get a list of products tied to broadcast with ID
   * @param {String}    id
   * @returns {?Object}
   */
  // eslint-disable-next-line
  getBroadcastProducts: async (id) => {
    const { data } = await ovpFetch(`/products/broadcast/${id}`);
    return data;
  },
  updateProductOnBroadcast: async (id, payload) => {
    await ovpFetch(`/products/broadcast/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
      iLikeItRaw: true,
    });
  },
  addProductsToBroadcast: async (id, arr) => {
    await ovpFetch(`/products/broadcast/${id}/import`, {
      method: 'POST',
      body: JSON.stringify(arr),
      iLikeItRaw: true,
    });
    return true;
  },
  scrapeProducts: async (url) => {
    const { data } = await ovpFetch('/products/scrape', {
      method: 'POST',
      body: JSON.stringify({
        url,
      }),
    });
    return data;
  },
  addScrapedProductToBroadcast: async (id, arr) => {
    const { data } = await ovpFetch(`/products/broadcast/${id}`, {
      method: 'POST',
      iLikeItRaw: true,
      body: JSON.stringify(arr),
    });

    return data;
  },
  sortProductsOnBroadcast: async (id, arr) => {
    await ovpFetch(`/products/broadcast/${id}/sort`, {
      method: 'POST',
      body: JSON.stringify(arr),
      iLikeItRaw: true,
    });
    return true;
  },
  removeProductsFromBroadcast: async (id, arr) => {
    await ovpFetch(`/products/broadcast/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(arr),
      iLikeItRaw: true,
    });
    return true;
  },
};

// eslint-disable-next-line
export { ProductsService };
